import type {ChildModel, UserModel} from 'app/modules/auth/core/_models'

/**
 * localStorage key holding the parent's currently selected child (`user.id`
 * of the student). Written by the header child switcher; read by the saga
 * (`appendIds` → `childId`), the legacy iframe (`studentUserId`) and every
 * child-scoped page.
 */
export const SELECTED_CHILD_STORAGE_KEY = 'selectedChildId'

/**
 * The child the parent is currently viewing. Falls back to the first child
 * when nothing is stored yet (first login) or the stored id no longer belongs
 * to this parent, so callers never see an empty selection.
 */
export const getSelectedChildId = (children?: ChildModel[] | null): string | null => {
  if (!children?.length) return null
  const stored = localStorage.getItem(SELECTED_CHILD_STORAGE_KEY)
  if (stored && children.some((child) => String(child.id) === stored)) return stored
  return String(children[0].id)
}

/** The selected child record, resolved with the same fallback as `getSelectedChildId`. */
export const getSelectedChild = (user?: Pick<UserModel, 'children'> | null): ChildModel | null => {
  const id = getSelectedChildId(user?.children)
  if (!id) return null
  return user?.children?.find((child) => String(child.id) === id) ?? null
}

/** Persist the parent's choice; the app reloads afterwards so every reader picks it up. */
export const setSelectedChildId = (id: string | number) => {
  localStorage.setItem(SELECTED_CHILD_STORAGE_KEY, String(id))
}

/**
 * Make sure a parent always has a selection stored, so requests fired before
 * the header mounts (dashboard widgets) already carry `childId`.
 */
export const ensureSelectedChild = (children?: ChildModel[] | null) => {
  const id = getSelectedChildId(children)
  if (id && localStorage.getItem(SELECTED_CHILD_STORAGE_KEY) !== id) {
    localStorage.setItem(SELECTED_CHILD_STORAGE_KEY, id)
  }
  return id
}

/**
 * Adds a parent's selected child as `childId` to a GET url (GETs carry no
 * body for the saga to append it to), unless the caller already set one.
 * The API verifies the child belongs to the parent. Other user types are
 * returned unchanged.
 */
export const appendChildIdToUrl = (
  userInfo: {userType?: string; user?: Pick<UserModel, 'children'> | null} | null | undefined,
  url: string
): string => {
  if (userInfo?.userType !== 'PARENT') return url
  if (/[?&]childId=/.test(url)) return url
  const childId = getSelectedChildId(userInfo.user?.children)
  if (!childId) return url
  return `${url}${url.includes('?') ? '&' : '?'}childId=${encodeURIComponent(childId)}`
}
